import React, { useMemo } from "react";
import { Badge } from "react-bootstrap";

import Constants from "../../constants";
import { AvailableScheduleDTO } from "../../models/EnrollmentHistoryDTO";
import { formatLocalDateTime } from "./format";
import { cursoCorto, sinAcentos } from "../../utils/cursoCorto";
import { horarioBonito } from "../../utils/horarioBonito";
import useEsTelefono from "../../utils/esTelefono";
import { GRUPOS_HORARIO, grupoHorario, maestroCorto, ordenarHorarios, periodoCorto, seLePuedeMatricular } from "../../navigation/academy/enrollStudentModal";

export interface SchedulePickerProps {
	schedules: AvailableScheduleDTO[] | null;
	picked: number[];
	onToggle: (schedule: AvailableScheduleDTO) => void;
	filter: string;
	onFilter: (value: string) => void;
	disabled?: boolean;
	title?: string;
}

/**
 * La tabla de horarios disponibles: la MISMA en "Matricular" del perfil del alumno y en el
 * alta por etapas. Vivía suelta en enrollStudentModal y se copió mal a la otra pantalla
 * (horas en 24h, columnas descuadradas); se extrajo aquí para que no vuelva a pasar
 * (Esteban, 2026-09-22).
 */
const SchedulePicker: React.FC<SchedulePickerProps> = ({ schedules, picked, onToggle, filter, onFilter, disabled, title }) => {
	const visibleSchedules = useMemo(() => {
		if (!schedules) {
			return [];
		}
		const ordenados = ordenarHorarios(schedules.filter(seLePuedeMatricular));
		// Se busca sin acentos y por palabras sueltas: "refri enero" encuentra
		// Refrigeración del período Enero aunque no estén juntas ni acentuadas.
		const terminos = sinAcentos(filter).split(/\s+/).filter((t) => t !== "");
		if (terminos.length < 1) {
			return ordenados;
		}
		return ordenados.filter((schedule) => {
			const texto = sinAcentos([
				schedule.courseName, schedule.levelName, schedule.name, schedule.code,
				schedule.periodName, schedule.sessions, schedule.mentorName,
				formatLocalDateTime(schedule.startDate)
			].filter(Boolean).join(" "));
			return terminos.every((t) => texto.includes(t));
		});
	}, [schedules, filter]);

	const isPicked = (id: number) => picked.includes(id);
	const movil = useEsTelefono();

	return (
		<>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
				<h6 style={{ margin: 0 }}>{title || Constants.Common.availableSchedules}</h6>
				<input className="form-control form-control-sm" style={{ maxWidth: movil ? undefined : 360 }} type="text" placeholder={Constants.Common.searchSchedules}
					value={filter} onChange={(event) => onFilter(event.target.value)} disabled={disabled} />
			</div>
			{schedules === null ? <div className="loader" /> : (visibleSchedules.length < 1
				? <div style={{ padding: 15, color: "var(--wa-apagado)" }}>{Constants.Common.noAvailableSchedules}</div>
				: movil ? (
					/*
						En el teléfono la tabla de ocho columnas obligaba a arrastrar de lado: cada horario
						es una tarjeta que se toca para elegirla (Esteban, 2026-09-23).
					*/
					<div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 420, overflow: "auto", paddingRight: 2 }}>
						{visibleSchedules.map((schedule, i) => (
							<React.Fragment key={"asmg_" + schedule.id}>
							{i === 0 || grupoHorario(visibleSchedules[i - 1]) !== grupoHorario(schedule)
								? <div className="waGrupoHorarios">{GRUPOS_HORARIO[grupoHorario(schedule)]}</div> : null}
							<button type="button" key={"asm_" + schedule.id} disabled={disabled} onClick={() => onToggle(schedule)}
								className={"waHorarioTarjeta" + (isPicked(schedule.id) ? " elegido" : "")} aria-pressed={isPicked(schedule.id)}>
								<span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
									<span style={{ display: "flex", gap: 10, alignItems: "center" }}>
										<input type="checkbox" readOnly tabIndex={-1} checked={isPicked(schedule.id)} style={{ pointerEvents: "none" }} />
										<b>{cursoCorto(schedule.courseName)}</b>
									</span>
									<Badge bg={schedule.eligibility === "REINGRESO" ? "info" : "success"} text={schedule.eligibility === "REINGRESO" ? "dark" : undefined} style={{ flexShrink: 0, fontWeight: 500 }}>
										{schedule.eligibility === "REINGRESO" ? Constants.Common.returningStudent : Constants.Common.newStudent}
									</Badge>
								</span>
								<span style={{ display: "block", fontSize: 14, paddingLeft: 26, marginTop: 2 }}>{horarioBonito(schedule.name, { plural: true, separador: " " })}</span>
								<span style={{ display: "block", fontSize: 13, color: "var(--wa-apagado)", paddingLeft: 26 }}>
									{periodoCorto(schedule.periodName)} · Inicia {formatLocalDateTime(schedule.startDate)}
									{maestroCorto(schedule) ? ` · ${maestroCorto(schedule)}` : ""}
								</span>
								<span style={{ display: "block", fontSize: 13, paddingLeft: 26, color: schedule.isFull ? "var(--wa-rojo)" : "var(--wa-apagado)" }}>
									Alumnos {schedule.enrolledStudents}/{schedule.studentsLimit}{schedule.isFull ? ` · ${Constants.Common.fullSchedule}` : ""}
								</span>
							</button>
							</React.Fragment>
						))}
					</div>
				) : (
					<div style={{ maxHeight: 300, overflow: "auto", border: "1px solid var(--wa-filo)", borderRadius: 6 }}>
						<table className="table table-sm table-hover" style={{ fontSize: 13, marginBottom: 0 }}>
							<thead style={{ position: "sticky", top: 0, background: "var(--wa-superficie)" }}>
								<tr>
									<th style={{ width: "44px", paddingLeft: "16px" }} />
									{/*
										Curso y Maestro se quedaban con el sobrante y aplastaban al resto:
										con un tope parten en dos renglones cuando hace falta.
										(Esteban, 2026-09-21)
									*/}
									<th style={{ width: "210px" }}>{Constants.Common.course}</th>
									{/*
										"Lunes a Viernes · 11:00 am – 3:00 pm" cabe en un renglon, y en una tabla
										de ancho automatico eso hace que esta columna se quede con todo el espacio
										sobrante y aplaste a Curso y Maestro. Con un ancho fijo el texto parte en
										dos renglones y el sobrante se lo reparten las demas.
										(Esteban, 2026-09-21)
									*/}
									<th style={{ width: "190px" }}>{Constants.Common.schedule}</th>
									<th>{Constants.Common.period}</th>
									<th>{Constants.Common.startsOn}</th>
									<th style={{ width: "170px" }}>{Constants.Common.mentor}</th>
									<th style={{ textAlign: "right", paddingRight: "22px" }}>Alumnos</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{visibleSchedules.map((schedule, i) => (
									<React.Fragment key={"asg_" + schedule.id}>
									{i === 0 || grupoHorario(visibleSchedules[i - 1]) !== grupoHorario(schedule)
										? <tr className="waGrupoHorarios"><td colSpan={8}>{GRUPOS_HORARIO[grupoHorario(schedule)]}</td></tr> : null}
									<tr key={"as_" + schedule.id} onClick={() => !disabled && onToggle(schedule)}
										style={{ cursor: "pointer", background: isPicked(schedule.id) ? "var(--wa-papel-3)" : undefined }}>
										{/* El clic en la casilla no debe subir al renglón: si sube, se marca y se desmarca en el mismo clic. */}
										<td onClick={(event) => event.stopPropagation()} style={{ paddingLeft: "16px" }}>
											<input type="checkbox" checked={isPicked(schedule.id)} onChange={() => onToggle(schedule)} disabled={disabled} />
										</td>
										<td>{cursoCorto(schedule.courseName)}</td>
										<td>{horarioBonito(schedule.name, { plural: true, separador: " " })}</td>
										<td style={{ whiteSpace: "nowrap" }}>{periodoCorto(schedule.periodName)}</td>
										<td style={{ whiteSpace: "nowrap" }}>{formatLocalDateTime(schedule.startDate)}</td>
										<td>{maestroCorto(schedule) || "-"}</td>
										<td style={{ textAlign: "right", whiteSpace: "nowrap", paddingRight: "22px", color: schedule.isFull ? "var(--wa-rojo)" : undefined }}>
											{schedule.enrolledStudents}/{schedule.studentsLimit}{schedule.isFull ? ` · ${Constants.Common.fullSchedule}` : ""}
										</td>
										<td>
											<Badge bg={schedule.eligibility === "REINGRESO" ? "info" : "success"} text={schedule.eligibility === "REINGRESO" ? "dark" : undefined}>
												{schedule.eligibility === "REINGRESO" ? Constants.Common.returningStudent : Constants.Common.newStudent}
											</Badge>
										</td>
									</tr>
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				))}
		</>
	);
};

export default SchedulePicker;
